import { useMemo, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { FormAccordionCard, CollapsibleSectionHeader } from '@/components/molecules';
import { TemplateAttachmentsSection } from '@/components/organisms/templates/TemplateAttachmentsSection';
import { TemplateDetailsSection } from '@/components/organisms/templates/TemplateDetailsSection';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { useCreateEstimateTemplate } from '@/hooks/estimateTemplates/useCreateEstimateTemplate';
import {
  tmOverrideUnderquotedServices,
  useEstimateLineItemState,
} from '@/hooks/estimates/useEstimateLineItemState';
import { useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { useHasCapability } from '@/hooks/usePermissions';
import type { CreateEstimateTemplateDto, EstimateTemplate } from '@/types/estimateTemplate';
import { FormPageLayout } from '@/components/layouts';
import { estimateTemplateEndpoints } from '@/lib/api/endpoints';
import { uploadStagedFiles } from '@/lib/sales/uploadStagedFiles';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import { EMPTY_TERMS_SELECTION, toTermsPayload, type TermsSelection } from '@/lib/terms';
import {
  templateDefaultsToDto,
  templateLineItemToDto,
  validateTemplateDefaults,
} from './templateLineItems';
import {
  EstimateTemplateDefaultsSection,
  EMPTY_TEMPLATE_DEFAULTS,
  type EstimateTemplateDefaultsValue,
} from '@/components/organisms/templates/EstimateTemplateDefaultsSection';
import { useEstimateTemplatePricing } from './useEstimateTemplatePricing';
import { DocumentLineItemsCard } from '@/components/organisms/sales/DocumentLineItemsCard';
import { documentLinePickers } from '@/components/organisms/sales/document-line-pickers';

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates/estimate/new')({
  component: NewEstimateTemplatePage,
});

function NewEstimateTemplatePage() {
  const router = useRouter();
  const createMutation = useCreateEstimateTemplate();
  const canManageTemplates = useHasCapability('can_manage_templates');
  const { data: laborRateProfiles = [] } = useLaborRateProfiles();

  const [sec, setSec] = useState({
    details: true,
    defaults: false,
    notes: false,
    attachments: false,
  });
  const toggle = (k: keyof typeof sec) => setSec((p) => ({ ...p, [k]: !p[k] }));

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [defaults, setDefaults] = useState<EstimateTemplateDefaultsValue>(EMPTY_TEMPLATE_DEFAULTS);
  // Business Units this template is offered to. EMPTY = every unit (a wildcard),
  // which is also the default, so a tenant that ignores the field keeps today's
  // behaviour exactly.
  const [businessUnitIds, setBusinessUnitIds] = useState<string[]>([]);

  // The estimate editor's own line state, so every control the estimate offers
  // works here identically — a template that cannot express what an estimate
  // expresses cannot produce that estimate.
  const lineState = useEstimateLineItemState();
  const { lineItems } = lineState;

  const { pricingMode, lineItemPreviews, subtotal, taxTotal, grandTotal } =
    useEstimateTemplatePricing(lineItems, defaults);

  /**
   * Services this template prices WITHOUT their parts — the shape a template
   * saved before the T&M switch materialized anything still holds, and which
   * every estimate built from it inherits. Named, never repaired on load: the
   * stored line prices are the template's content, and moving them is an edit
   * its author makes.
   */
  const underquotedTMServices = useMemo(
    () =>
      pricingMode === 'TM_OVERRIDE'
        ? tmOverrideUnderquotedServices(lineItems).map((line) => line.name)
        : [],
    [pricingMode, lineItems],
  );

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    if (lineItems.some((li) => !li.code && !li.name.trim())) {
      toast.error('Name each custom line item');
      return;
    }

    const defaultsError = validateTemplateDefaults(defaults, lineItems);
    if (defaultsError) {
      toast.error(defaultsError);
      return;
    }

    const dto: CreateEstimateTemplateDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      ...toTermsPayload(terms),
      // The SAME mapper the estimate saves through, so a template stores every
      // field the estimate it produces would have stored.
      lineItems: lineItems.map((li, index) =>
        templateLineItemToDto(li, index, lineItemPreviews[index]),
      ),
      subtotal,
      taxTotal,
      total: grandTotal,
      businessUnitIds,
      ...templateDefaultsToDto(defaults),
    };

    createMutation.mutate(dto, {
      onSuccess: async (newTemplate: EstimateTemplate) => {
        await uploadStagedFiles(
          stagedFiles,
          estimateTemplateEndpoints.attachmentUploadUrl(newTemplate.id),
        );
        void router.navigate({ to: '/dashboard/settings/templates' });
      },
    });
  };

  if (!canManageTemplates) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-sm text-muted-foreground">
          You do not have permission to create templates.
        </p>
      </div>
    );
  }

  return (
    <FormPageLayout
      title="New Estimate Template"
      onBack={() => router.history.back()}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => router.history.back()}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Create
          </Button>
        </>
      }
    >
      <div className="min-h-full bg-muted/40">
        <div className="mx-auto max-w-6xl space-y-3 px-4 py-5">
          <FormAccordionCard
            open={sec.details}
            header={
              <CollapsibleSectionHeader
                label="Details"
                open={sec.details}
                onToggle={() => toggle('details')}
                badge="required"
              />
            }
            bodyClassName="space-y-4"
          >
            <TemplateDetailsSection
              name={name}
              onNameChange={setName}
              placeholder="AC Replacement Template"
              businessUnitIds={businessUnitIds}
              onBusinessUnitIdsChange={setBusinessUnitIds}
            />
          </FormAccordionCard>

          <DocumentLineItemsCard
            description="Build the template from pricebook or custom items."
            lineItemsProps={{
              ...documentLinePickers({
                onAdd: lineState.addPickedLineItem,
                onAddConfiguredPart: lineState.addConfiguredServicePart,
                lineItems,
              }),
              lineItems,
              lineItemPreviews,
              // Driven by the template's own mode: choosing time & materials has
              // to switch the line editor into T&M rules, or the author can never
              // enter the hours that mode then requires.
              pricingMode,
              removeConfiguredPart: lineState.removeConfiguredServicePart,
              updateQuantity: lineState.updateQuantity,
              updateName: lineState.updateName,
              updateType: lineState.updateType,
              updateUnit: lineState.updateUnit,
              updateBasePrice: lineState.updateBasePrice,
              updateManualPriceOverride: lineState.updateManualPriceOverride,
              updatePriceStrategy: lineState.updatePriceStrategy,
              updateLaborRate: lineState.updateLaborRate,
              updateEstimatedHours: lineState.updateEstimatedHours,
              updateLaborCostLines: lineState.updateLaborCostLines,
              laborRateProfiles,
              updateDynamicPricingConfig: lineState.updateDynamicPricingConfig,
              updateDynamicMarkupProfile: lineState.updateDynamicMarkupProfile,
              updateDescription: lineState.updateDescription,
              updateComponentDisplayMode: lineState.updateComponentDisplayMode,
              updateMarkupPercent: lineState.updateMarkupPercent,
              updateCostBasis: lineState.updateCostBasis,
              updateTaxable: lineState.updateTaxable,
              updateTaxAccounting: lineState.updateTaxAccounting,
              updateMemberPricing: lineState.updateMemberPricing,
              onSaveLineToPricebook: lineState.linkLineToCatalogItem,
              updateChargeable: lineState.updateChargeable,
              updateHiddenFromCustomer: lineState.updateHiddenFromCustomer,
              toggleOptional: lineState.toggleOptional,
              toggleSelected: lineState.toggleSelected,
              removeLineItem: lineState.removeLineItem,
              onReorder: lineState.reorderLineItems,
              // A template is authored for many customers, so nothing here can
              // depend on one customer's membership.
              membershipEligibleRowIds: new Set<string>(),
              membershipInfo: null,
            }}
            costPanelProps={{ lineItems, pricingMode, subtotal, taxTotal, grandTotal }}
          />

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Optional
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormAccordionCard
            open={sec.defaults}
            header={
              <CollapsibleSectionHeader
                label="Document defaults"
                open={sec.defaults}
                onToggle={() => toggle('defaults')}
                badge="optional"
              />
            }
            bodyClassName="space-y-4"
          >
            {/* What every estimate built from this template starts with. Each
                one left blank simply says nothing, and the estimate keeps its
                own default. */}
            <EstimateTemplateDefaultsSection
              value={defaults}
              tmUnderquotedServices={underquotedTMServices}
              onTMRepriceFromParts={() => lineState.applyPricingMode('TM_OVERRIDE')}
              onChange={(patch) => {
                setDefaults((prev) => ({ ...prev, ...patch }));
                // The T&M switch is not only a default the estimate inherits —
                // it is the mode THESE lines are edited and priced under
                // (`templateEditorPricingMode`). Left to the flag alone, a
                // service here is stored in the shape neither engine can price
                // from its parts, and every estimate built from this template
                // inherits it. Materializing on the switch is what the estimate
                // editor does with the same control (`applyPricingMode`).
                if (patch.pricingMode !== undefined) {
                  lineState.applyPricingMode(patch.pricingMode ?? 'STANDARD');
                }
              }}
            />
          </FormAccordionCard>

          <FormAccordionCard
            open={sec.notes}
            header={
              <CollapsibleSectionHeader
                label="Notes"
                open={sec.notes}
                onToggle={() => toggle('notes')}
                badge="optional"
                statusBadge={
                  description.trim() || notes.trim() || terms.termsMode !== 'DEFAULT'
                    ? 'Added'
                    : undefined
                }
              />
            }
            bodyClassName="space-y-4"
          >
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Reusable lineup for standard replacements"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes for when this template should be used"
                rows={3}
                className="text-sm"
              />
            </div>
            {/* Cascades onto every estimate built from this template; the
                estimate can still override before it goes out. */}
            <TermsSelector value={terms} onChange={setTerms} />
          </FormAccordionCard>

          <FormAccordionCard
            open={sec.attachments}
            header={
              <CollapsibleSectionHeader
                label="Attachments"
                open={sec.attachments}
                onToggle={() => toggle('attachments')}
                badge="optional"
                statusBadge={stagedFiles.length > 0 ? 'Added' : undefined}
              />
            }
            bodyClassName="space-y-4"
          >
            <TemplateAttachmentsSection
              kind="estimate"
              mode="staging"
              stagedFiles={stagedFiles}
              onStagedFilesChange={setStagedFiles}
            />
          </FormAccordionCard>
        </div>
      </div>
    </FormPageLayout>
  );
}
